import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Container from 'ls-common-client/dist/components/Container';
import LazyLoad from '../../../components/LazyLoad';
import RoundButton from '../../../components/RoundButton';
import Truncate from '../../../components/Truncate';
import readingTime from '../../../utils/readingTime';
import PlaceholderJpg from '../../../assets/defaultProfileImage.jpg';

const StyledArticlesListRoot = styled(Container)`
  padding: 50px 0 100px;
  width: 100%;
`;

const StyledArticlesListContent = styled(Container)`
  width: 100%;
`;

const StyledArticlesListFeatured = styled(Container)`
  .CategoryGuide__ArticlesList__col {
    width: 100%;
    max-width: 100%;
  }
  .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__item {
    display: flex;
    flex-direction: row;
    margin-bottom: 40px;
    align-items: center;
  }
  .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__ItemContent {
    max-width: 378px;
  }
  .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__image {
    min-width: 670px;
    margin-right: 80px;
    height: 529px;
    max-width: 65%;
  }
  .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__title {
    font-size: 40px;
    margin-bottom: 35px;
  }
  .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__date {
    font-size: 20px;
  }
  .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__blurb {
    margin-bottom: 45px;
  }
  @media (max-width: 1024px) {
    .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__image {
      min-width: 50%;
      max-width: 50%;
      margin-right: 60px;
      height: 350px;
    }

    .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__title {
      font-size: 30px;
    }

    .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__date {
      font-size: 16px;
    }
  }

  @media (max-width: 767px) {
    .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__item {
      flex-direction: column;
    }

    .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__image {
      min-width: 100%;
      margin-right: 0;
    }

    .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__image,
    .CategoryGuide__ArticlesList__image {
      height: 298px;
    }

    .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__title {
      font-size: 28px;
      margin-bottom: 25px;
    }

    .CategoryGuide__ArticlesList__col
      .CategoryGuide__ArticlesList__ItemContent {
      max-width: 100%;
    }

    .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__blurb {
      margin-bottom: 30px;
    }

    .CategoryGuide__ArticlesList__col .CategoryGuide__ArticlesList__date {
      font-size: 16px;
    }
  }
`;

const StyledHeading = styled.h2`
  font-size: 26px;
  margin-bottom: 40px;
  font-weight: bold;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const StyledList = styled(Container)`
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-wrap: wrap;
`;

const StyledColumn = styled(Container)`
  max-width: 378px;
  min-width: 378px;
  cursor: pointer;
  margin-right: 22px;
  line-height: 1.2;
  margin-bottom: 40px;

  @media (max-width: 1024px) {
    max-width: 47%;
    min-width: 47%;
  }

  @media (max-width: 767px) {
    max-width: 100%;
    min-width: 100%;
    cursor: pointer;
    margin-right: 22px;
    line-height: 1.2;
    margin-bottom: 40px;
  }
`;

const StyledItem = styled(Container)`
  width: 100%;
  height: auto;
  display: flex;
  position: relative;
  align-items: flex-start;
  flex-direction: column;
  background-size: cover;
  background-position: center;

  .CategoryGuide__ArticlesList__hover {
    opacity: 0;
    top: 50%;
    left: 50%;
    width: 200px;
    height: 44px;
    position: absolute;
    transform: translate(-50%, -50%);
    transition: all 0.3s ease;
  }
`;

const StyledLink = styled(Link)`
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;
  z-index: 2;
  overflow: hidden;
  position: absolute;
  text-indent: -999999px;

  &:hover
    + .CategoryGuide__ArticlesList__image
    .CategoryGuide__ArticlesList__hover {
    opacity: 1;
  }
`;

const StyledImage = styled(LazyLoad)`
  height: 298px;
  overflow: hidden;
  position: relative;
  width: 100%;
  border-radius: 8px;
  margin-bottom: 20px;
  img {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`;

const StyledItemTitle = styled.h3`
  color: ${props => props.theme.text.normal};
  width: 100%;
  z-index: 1;
  position: relative;
  font-size: 18px;
  max-width: 100%;
  min-height: 35px;
  font-family: 'Circular';
  font-weight: bold;
  line-height: 1.2;
  margin-bottom: 20px;
  @media (max-width: 767px) {
    min-height: 0;
  }
`;

const StyledItemBlurb = styled(Container)`
  font-size: 16px;
  color: ${props => props.theme.text.normal};
  margin-bottom: 20px;
`;

const StyledItemMeta = styled(Container)`
  display: flex;

  .CategoryGuide__ArticlesList__date {
    font-size: 14px;
    line-height: 1.21;
    color: #757575;
    margin-bottom: 0;
    margin-top: 0;
  }
`;

const ArticlesList = ({ categoryName = '', articles }) => {
  return (
    <StyledArticlesListRoot className="CategoryGuide__ArticlesList__Root">
      <StyledArticlesListContent className="CategoryGuide__ArticlesList__content">
        <StyledArticlesListFeatured className="CategoryGuide__ArticlesList__Featured">
          {articles
            .slice(0, 1)
            .map(
              (
                {
                  article: {
                    articleTitle,
                    urlSlug,
                    featureImageUrl,
                    articleBlurb,
                    wordCount,
                  },
                },
                key
              ) => (
                <StyledColumn
                  key={key.toString()}
                  className="CategoryGuide__ArticlesList__col"
                >
                  <StyledItem className="CategoryGuide__ArticlesList__item">
                    <StyledLink
                      rel="noopener noreferrer"
                      className="CategoryGuide__ArticlesList__link"
                      to={urlSlug}
                    >
                      {articleTitle}
                    </StyledLink>
                    <StyledImage className="CategoryGuide__ArticlesList__image">
                      <img
                        src={featureImageUrl || PlaceholderJpg}
                        alt={articleTitle}
                        onError={e => {
                          e.target.onerror = null;
                          e.target.src = PlaceholderJpg;
                        }}
                      />
                      <RoundButton
                        className="CategoryGuide__ArticlesList__hover"
                        tag="div"
                        theme="outlineBlue"
                      >
                        Take me to this article
                      </RoundButton>
                    </StyledImage>
                    <Container className="CategoryGuide__ArticlesList__ItemContent">
                      <Container className="CategoryGuide__ArticlesList__titleWrapper">
                        <Truncate lines={2}>
                          <StyledItemTitle className="CategoryGuide__ArticlesList__title">
                            {articleTitle}
                          </StyledItemTitle>
                        </Truncate>
                      </Container>
                      <StyledItemBlurb className="CategoryGuide__ArticlesList__blurb">
                        <Truncate lines={5}>
                          <Container>{articleBlurb}</Container>
                        </Truncate>
                      </StyledItemBlurb>
                      <StyledItemMeta className="CategoryGuide__ArticlesList__meta">
                        <Container>
                          <p className="CategoryGuide__ArticlesList__date">
                            {readingTime(wordCount)}
                          </p>
                        </Container>
                      </StyledItemMeta>
                    </Container>
                  </StyledItem>
                </StyledColumn>
              )
            )}
        </StyledArticlesListFeatured>
        {!!articles.slice(1, 7).length && (
          <>
            <StyledHeading className="CategoryGuide__ArticlesList__heading">
              {categoryName} Guides & Resources
            </StyledHeading>
            <StyledList className="CategoryGuide__ArticlesList__List">
              {articles
                .slice(1, 7)
                .map(
                  (
                    {
                      article: {
                        articleTitle,
                        urlSlug,
                        featureImageUrl,
                        articleBlurb,
                        wordCount,
                      },
                    },
                    key
                  ) => (
                    <StyledColumn
                      key={key.toString()}
                      className="CategoryGuide__ArticlesList__col"
                    >
                      <StyledItem className="CategoryGuide__ArticlesList__item">
                        <StyledLink
                          rel="noopener noreferrer"
                          className="CategoryGuide__ArticlesList__link"
                          to={urlSlug}
                        >
                          {articleTitle}
                        </StyledLink>
                        <StyledImage className="CategoryGuide__ArticlesList__image">
                          <img
                            src={featureImageUrl || PlaceholderJpg}
                            alt={articleTitle}
                            onError={e => {
                              e.target.onerror = null;
                              e.target.src = PlaceholderJpg;
                            }}
                          />
                          <RoundButton
                            className="CategoryGuide__ArticlesList__hover"
                            tag="div"
                            theme="outlineBlue"
                          >
                            Take me to this article
                          </RoundButton>
                        </StyledImage>
                        <Container className="CategoryGuide__ArticlesList__ItemContent">
                          <Container className="CategoryGuide__ArticlesList__titleWrapper">
                            <Truncate lines={2}>
                              <StyledItemTitle className="CategoryGuide__ArticlesList__title">
                                {articleTitle}
                              </StyledItemTitle>
                            </Truncate>
                          </Container>
                          <StyledItemBlurb className="CategoryGuide__ArticlesList__blurb">
                            <Truncate lines={5}>
                              <div>{articleBlurb}</div>
                            </Truncate>
                          </StyledItemBlurb>
                          <StyledItemMeta className="CategoryGuide__ArticlesList__meta">
                            <Container>
                              <p className="CategoryGuide__ArticlesList__date">
                                {readingTime(wordCount)}
                              </p>
                            </Container>
                          </StyledItemMeta>
                        </Container>
                      </StyledItem>
                    </StyledColumn>
                  )
                )}
            </StyledList>
          </>
        )}
        {!!articles.slice(7, articles.length).length && (
          <>
            <StyledHeading className="CategoryGuide__ArticlesList__heading">
              {categoryName} Informational Articles
            </StyledHeading>
            <StyledList className="CategoryGuide__ArticlesList__List">
              {articles
                .slice(7, articles.length)
                .map(
                  (
                    {
                      article: {
                        articleTitle,
                        urlSlug,
                        featureImageUrl,
                        articleBlurb,
                        wordCount,
                      },
                    },
                    key
                  ) => (
                    <StyledColumn
                      key={key.toString()}
                      className="CategoryGuide__ArticlesList__col"
                    >
                      <StyledItem className="CategoryGuide__ArticlesList__item">
                        <StyledLink
                          rel="noopener noreferrer"
                          className="CategoryGuide__ArticlesList__link"
                          to={urlSlug}
                        >
                          {articleTitle}
                        </StyledLink>
                        <StyledImage className="CategoryGuide__ArticlesList__image">
                          <img
                            src={featureImageUrl || PlaceholderJpg}
                            alt={articleTitle}
                            onError={e => {
                              e.target.onerror = null;
                              e.target.src = PlaceholderJpg;
                            }}
                          />
                          <RoundButton
                            className="CategoryGuide__ArticlesList__hover"
                            tag="div"
                            theme="outlineBlue"
                          >
                            Take me to this article
                          </RoundButton>
                        </StyledImage>
                        <Container className="CategoryGuide__ArticlesList__ItemContent">
                          <Container className="CategoryGuide__ArticlesList__titleWrapper">
                            <Truncate lines={2}>
                              <StyledItemTitle className="CategoryGuide__ArticlesList__title">
                                {articleTitle}
                              </StyledItemTitle>
                            </Truncate>
                          </Container>
                          <StyledItemBlurb className="CategoryGuide__ArticlesList__blurb">
                            <Truncate lines={5}>
                              <div>{articleBlurb}</div>
                            </Truncate>
                          </StyledItemBlurb>
                          <StyledItemMeta className="CategoryGuide__ArticlesList__meta">
                            <Container>
                              <p className="CategoryGuide__ArticlesList__date">
                                {readingTime(wordCount)}
                              </p>
                            </Container>
                          </StyledItemMeta>
                        </Container>
                      </StyledItem>
                    </StyledColumn>
                  )
                )}
            </StyledList>
          </>
        )}
      </StyledArticlesListContent>
    </StyledArticlesListRoot>
  );
};

export default ArticlesList;
